import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import Sidebar from '@/components/navigation/Sidebar';
import Home from '@/pages/Home';
import LoginPage from '@/pages/auth/LoginPage';
import SSOCallbackPage from '@/pages/auth/SSOCallbackPage';
import SchoolmsLaunchPage from '@/pages/auth/SchoolmsLaunchPage';
import SSORegisterPage from '@/pages/auth/SSORegisterPage';
import DashboardPage from '@/pages/DashboardPage';
import CoursesPage from '@/pages/CoursesPage';
import CreateCoursePage from '@/pages/CreateCoursePage';
import EditCoursePage from '@/pages/EditCoursePage';
import CourseContentPage from '@/pages/CourseContentPage';
import CourseLearningPage from '@/pages/CourseLearningPage';
import MediaLibraryPage from '@/pages/MediaLibraryPage';
import AuditLogPage from '@/pages/AuditLogPage';
import ReportsPage from '@/pages/ReportsPage';
import SpacesPage from '@/pages/SpacesPage';
import InviteAcceptPage from '@/pages/InviteAcceptPage';
import EnrollmentsPage from '@/pages/EnrollmentsPage';
import ExamsPage from '@/pages/ExamsPage';
import CreateExamPage from '@/pages/CreateExamPage';
import EditExamPage from '@/pages/EditExamPage';
import TakeExamPage from '@/pages/TakeExamPage';
import CertificatesPage from '@/pages/CertificatesPage';
import LeaderboardPage from '@/pages/LeaderboardPage';
import BadgesPage from '@/pages/BadgesPage';
import ProgressPage from '@/pages/ProgressPage';
import Profile from '@/pages/Profile';
import './index.css';

function ProtectedLayout({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar />
      <main className="flex-1 min-h-0 min-w-0 overflow-y-auto">{children}</main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/sso-callback" element={<SSOCallbackPage />} />
        <Route path="/auth/schoolms" element={<SchoolmsLaunchPage />} />
        <Route path="/auth/sso-register" element={<SSORegisterPage />} />
        <Route path="/invites/:token" element={<InviteAcceptPage />} />

        <Route
          path="/*"
          element={
            <ProtectedLayout>
              <Routes>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/courses" element={<CoursesPage />} />
                <Route path="/courses/create" element={<CreateCoursePage />} />
                <Route path="/courses/:id" element={<CourseLearningPage />} />
                <Route path="/courses/:id/edit" element={<EditCoursePage />} />
                <Route path="/courses/:id/content" element={<CourseContentPage />} />
                <Route path="/enrollments" element={<EnrollmentsPage />} />
                <Route path="/exams" element={<ExamsPage />} />
                <Route path="/exams/create" element={<CreateExamPage />} />
                <Route path="/exams/:id/edit" element={<EditExamPage />} />
                <Route path="/exams/:id" element={<TakeExamPage />} />
                <Route path="/certificates" element={<CertificatesPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/badges" element={<BadgesPage />} />
                <Route path="/progress" element={<ProgressPage />} />
                <Route path="/media" element={<MediaLibraryPage />} />
                <Route path="/spaces" element={<SpacesPage />} />
                <Route path="/audit" element={<AuditLogPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </ProtectedLayout>
          }
        />
      </Routes>
    </AuthProvider>
  );
}

export default App;
